import { Component, OnInit, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { environment } from '../../../../environments/environment';
import { MediaService } from '../../services/media.service';
import { ModalService } from '../../services/modal.service';

@Component({
  selector: 'app-signal-media-modal',
  templateUrl: './signal-media-modal.component.html',
  styleUrls: ['./signal-media-modal.component.scss']
})
export class SignalMediaModalComponent implements OnInit {
  title: string = 'Mídia do Sinal';
  activeTab: 'pdf' | 'image' | 'video' = 'pdf';
  isValidated: boolean = false;
  hasPermission: boolean = true;
  fakeUser: boolean = false;

  pdfs: any[] = [];
  selectedPdfIndex: number = 0;
  pdfSrc: SafeResourceUrl;
  pdfsInfoLoaded: boolean = false;
  pdfCache: { [id: string]: SafeResourceUrl } = {};

  images: any[] = [];
  selectedImageIndex: number = 0;
  imageSrc: SafeResourceUrl;
  imagesInfoLoaded: boolean = false;
  imageCache: { [id: string]: SafeResourceUrl } = {};
  showImageModal: boolean = false;

  videos: any[] = [];
  selectedVideoIndex: number = 0;
  videoSrc: SafeResourceUrl;
  videoCache: { [id: string]: SafeResourceUrl } = {};

  loading = {
    pdfInfo: false,
    pdf: false,
    imageInfo: false,
    image: false,
    video: false
  };

  constructor(
    private dialogRef: MatDialogRef<SignalMediaModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private mediaService: MediaService,
    private sanitizer: DomSanitizer,
    private modalService: ModalService,
    private router: Router
  ) { }

  ngOnInit(): void {
    this.isValidated = this.data.signal && this.data.signal.sinalValidado === true;
    this.hasPermission = this.data.hasPermission !== undefined ? this.data.hasPermission : true;
    this.fakeUser = this.data.fakeUser || false;

    if (this.data.signal && this.data.signal.signalName) {
      this.title = `Mídia - ${this.data.signal.signalName}`;
    }

    if (this.data.initialTab) {
      this.activeTab = this.data.initialTab;
    } else {
      this.setDefaultActiveTab();
    }

    this.loadMetadataForActiveTab();
  }

  loadMetadataForActiveTab(): void {
    switch (this.activeTab) {
      case 'pdf':
        this.loadPdfInfo();
        break;
      case 'image':
        this.loadImageInfo();
        break;
      case 'video':
        this.loadVideosInfo();
        break;
    }
  }

  goToPlans(): void {
    this.dialogRef.close();
    this.router.navigate([`${environment.url_version}/planos`]);
  }

  close(): void {
    for (const id in this.pdfCache) {
      URL.revokeObjectURL(this.pdfCache[id].toString());
    }
    for (const id in this.imageCache) {
      URL.revokeObjectURL(this.imageCache[id].toString());
    }

    this.dialogRef.close();
  }

  hasTabs(): boolean {
    return this.hasPdfs() || this.hasImages() || this.hasVideos();
  }

  hasPdfs(): boolean {
    return this.data.signal && this.data.signal.pdfId && this.data.signal.pdfId.length > 0;
  }

  hasImages(): boolean {
    if (!this.data.signal) return false;
    if (this.data.signal.sinalValidado === true) {
      const hasExtraFotos = this.data.signal.fotoId && this.data.signal.fotoId.length > 1;
      const hasSignalPhoto = !!this.data.signal.photo;
      return hasExtraFotos || hasSignalPhoto;
    }
    return this.data.signal.fotoId && this.data.signal.fotoId.length > 0;
  }

  hasVideos(): boolean {
    return this.data.signal && this.data.signal.videos && this.data.signal.videos.length > 0;
  }

  setDefaultActiveTab(): void {
    if (this.hasPdfs()) {
      this.activeTab = 'pdf';
    } else if (this.hasImages()) {
      this.activeTab = 'image';
    } else if (this.hasVideos()) {
      this.activeTab = 'video';
    }
  }

  setActiveTab(tab: 'pdf' | 'image' | 'video'): void {
    if (this.activeTab !== tab) {
      this.activeTab = tab;
      this.loadMetadataForActiveTab();
    }
  }

  loadPdfInfo(): void {
    if (!this.hasPdfs() || this.pdfsInfoLoaded) return;

    this.pdfsInfoLoaded = true;
    this.loading.pdfInfo = true;
    const pdfRequests = [];
    this.data.signal.pdfId.forEach((pdfId, index) => {
      this.mediaService.getPdfInfo(pdfId).subscribe(
        (response: any) => {
          if (response && response.PDFs && response.PDFs.length > 0) {
            this.pdfs.push(...response.PDFs);
            if (index === 0 && this.pdfs.length === response.PDFs.length) {
              this.selectedPdfIndex = 0;
              this.loadPdfContent(this.pdfs[0]._id);
            }
          }
          if (index === this.data.signal.pdfId.length - 1) {
            this.loading.pdfInfo = false;
          }
        },
        error => {
          console.error('Error loading PDF info:', error);
          this.loading.pdfInfo = false;
          if (index === 0) {
            this.modalService.openModal('Erro', 'Não foi possível carregar as informações do PDF', 'error', 'Ok').subscribe();
          }
        }
      );
    });
  }

  selectPdf(index: number): void {
    if (this.selectedPdfIndex === index) return;

    this.selectedPdfIndex = index;
    const pdfId = this.pdfs[index]._id;

    if (this.pdfCache[pdfId]) {
      this.pdfSrc = this.pdfCache[pdfId];
    } else {
      this.loadPdfContent(pdfId);
    }
  }

  get currentPdf(): any {
    return this.pdfs[this.selectedPdfIndex];
  }

  loadPdfContent(pdfId: string): void {
    if (this.pdfCache[pdfId]) {
      this.pdfSrc = this.pdfCache[pdfId];
      return;
    }

    this.loading.pdf = true;
    this.pdfSrc = null;

    this.mediaService.downloadPdf(pdfId).subscribe(
      (response: any) => {
        const blob = new Blob([response], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        const safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);

        this.pdfCache[pdfId] = safeUrl;
        this.pdfSrc = safeUrl;

        this.loading.pdf = false;
      },
      error => {
        console.error('Error downloading PDF:', error);
        this.loading.pdf = false;
        this.modalService.openModal('Erro', 'Não foi possível carregar o PDF', 'error', 'Ok').subscribe();
      }
    );
  }

  loadImageInfo(): void {
    if (!this.hasImages() || this.imagesInfoLoaded) return;

    this.imagesInfoLoaded = true;
    this.loading.imageInfo = true;

    const isValidated = this.data.signal.sinalValidado === true;

    if (isValidated && this.data.signal.photo) {
      const base64Url = 'data:image/jpeg;base64,' + this.data.signal.photo;
      const safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(base64Url);
      this.images.push({ _id: '__signal_photo__', titulo: 'Foto do Sinal' });
      this.imageCache['__signal_photo__'] = safeUrl;
      this.selectedImageIndex = 0;
      this.imageSrc = safeUrl;
    }

    const fotoIds: string[] = this.data.signal.fotoId || [];
    const idsToLoad = isValidated ? fotoIds.slice(1) : fotoIds;

    if (idsToLoad.length === 0) {
      this.loading.imageInfo = false;
      return;
    }

    idsToLoad.forEach((imageId, index) => {
      this.mediaService.getImageInfo(imageId).subscribe(
        (response: any) => {
          if (response && response.fotos && response.fotos.length > 0) {
            this.images.push(...response.fotos);

            if (!this.imageSrc && this.images.length > 0) {
              this.selectedImageIndex = 0;
              this.loadImageContent(this.images[0]._id);
            }
          }

          if (index === idsToLoad.length - 1) {
            this.loading.imageInfo = false;
          }
        },
        error => {
          console.error('Error loading image info:', error);
          this.loading.imageInfo = false;
          if (index === 0) {
            this.modalService.openModal('Erro', 'Não foi possível carregar as informações da imagem', 'error', 'Ok').subscribe();
          }
        }
      );
    });
  }

  selectImage(index: number): void {
    if (this.selectedImageIndex === index) return;

    this.selectedImageIndex = index;
    const imageId = this.images[index]._id;

    if (this.imageCache[imageId]) {
      this.imageSrc = this.imageCache[imageId];
    } else {
      this.loadImageContent(imageId);
    }
  }

  get currentImage(): any {
    return this.images[this.selectedImageIndex];
  }

  loadImageContent(imageId: string): void {
    if (this.imageCache[imageId]) {
      this.imageSrc = this.imageCache[imageId];
      return;
    }

    this.loading.image = true;
    this.imageSrc = null;

    this.mediaService.downloadImage(imageId).subscribe(
      (response: any) => {
        const blob = new Blob([response], { type: 'image/jpeg' });
        const url = URL.createObjectURL(blob);
        const safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);

        this.imageCache[imageId] = safeUrl;
        this.imageSrc = safeUrl;

        this.loading.image = false;
      },
      error => {
        console.error('Error downloading image:', error);
        this.loading.image = false;
        this.modalService.openModal('Erro', 'Não foi possível carregar a imagem', 'error', 'Ok').subscribe();
      }
    );
  }

  closeImageModal(): void {
    this.showImageModal = false;
  }

  loadVideosInfo(): void {
    if (!this.hasVideos()) return;

    this.videos = this.data.signal.videos;

    if (this.videos.length > 0) {
      this.selectedVideoIndex = 0;
      this.loadVideoContent(this.videos[0].idYoutube);
    }
  }

  selectVideo(index: number): void {
    if (this.selectedVideoIndex === index) return;

    this.selectedVideoIndex = index;
    const videoId = this.videos[index].idYoutube;

    if (this.videoCache[videoId]) {
      this.videoSrc = this.videoCache[videoId];
    } else {
      this.loadVideoContent(videoId);
    }
  }

  get currentVideo(): any {
    return this.videos[this.selectedVideoIndex];
  }

  loadVideoContent(youtubeId: string): void {
    if (!youtubeId) {
      this.videoSrc = null;
      return;
    }

    if (this.videoCache[youtubeId]) {
      this.videoSrc = this.videoCache[youtubeId];
      return;
    }

    this.loading.video = true;

    const embedUrl = `https://www.youtube.com/embed/${youtubeId}`;
    const safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(embedUrl);

    this.videoCache[youtubeId] = safeUrl;
    this.videoSrc = safeUrl;

    this.loading.video = false;
  }
}